if (!customElements.get('collections-directory')) {
  class CollectionsDirectory extends HTMLElement {
    connectedCallback() {
      this.searchInput = this.querySelector('[data-collections-search]');
      this.clearButton = this.querySelector('[data-collections-clear]');
      this.resetButton = this.querySelector('[data-collections-reset]');
      this.status = this.querySelector('[data-collections-status]');
      this.emptyState = this.querySelector('[data-collections-empty]');
      this.pagination = this.querySelector('.js-pagination')?.closest('nav');
      this.grid = this.querySelector('custom-pagination > ul, :scope > .container > ul');
      this.loadPromise = null;

      if (!this.searchInput || !this.grid) return;

      this.searchInput.addEventListener('input', () => this.handleInput());
      this.searchInput.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.searchInput.value) this.clearSearch();
      });
      this.searchInput.addEventListener('focus', () => this.loadAllCollections());
      this.clearButton?.addEventListener('click', () => this.clearSearch());
      this.resetButton?.addEventListener('click', () => this.clearSearch());
    }

    async handleInput() {
      this.update();
      if (this.searchInput.value.trim()) {
        await this.loadAllCollections();
        this.update();
      }
    }

    async loadAllCollections() {
      if (this.loadPromise) return this.loadPromise;

      const nextLink = this.querySelector('.js-pagination-load-more[href]');
      if (!nextLink) return Promise.resolve();

      this.loadPromise = this.fetchRemainingPages(nextLink.href);
      return this.loadPromise;
    }

    async fetchRemainingPages(nextUrl) {
      const paginationElement = this.querySelector('custom-pagination');
      if (paginationElement) paginationElement.dataset.pauseInfiniteScroll = 'true';

      const knownHandles = new Set(
        this.collectionItems.map((item) => item.dataset.collectionHandle)
      );

      try {
        while (nextUrl) {
          const response = await fetch(nextUrl);
          if (!response.ok) throw new Error(response.status);

          const documentHtml = new DOMParser().parseFromString(await response.text(), 'text/html');
          const source = documentHtml.querySelector('[data-collections-directory]');
          if (!source) break;

          source.querySelectorAll('[data-collection-item]').forEach((item) => {
            const handle = item.dataset.collectionHandle;
            if (!knownHandles.has(handle)) {
              knownHandles.add(handle);
              const query = this.searchInput.value.trim().toLocaleLowerCase();
              item.hidden = Boolean(query) && !item.dataset.collectionTitle.includes(query);
              this.grid.insertAdjacentHTML('beforeend', item.outerHTML);
            }
          });

          const nextPath = source.querySelector('.js-pagination-load-more[href]')?.getAttribute('href');
          nextUrl = nextPath ? new URL(nextPath, window.location.origin).href : '';
        }

        this.pagination?.remove();
      } catch (error) {
        console.warn('Unable to load all collections for filtering.', error);
      }
    }

    get collectionItems() {
      return Array.from(this.querySelectorAll('[data-collection-item]'));
    }

    update() {
      const query = this.searchInput.value.trim().toLocaleLowerCase();
      let visibleCount = 0;

      this.collectionItems.forEach((item) => {
        const isVisible = !query || item.dataset.collectionTitle.includes(query);
        item.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      this.querySelectorAll('.js-pagination-result:not([data-collection-item])').forEach((item) => {
        item.hidden = Boolean(query);
      });

      if (this.clearButton) this.clearButton.hidden = !query;
      if (this.emptyState) this.emptyState.hidden = !query || visibleCount > 0;
      if (this.status) {
        this.status.hidden = !query;
        this.status.textContent = query
          ? `${visibleCount} ${visibleCount === 1 ? 'collection' : 'collections'} found`
          : '';
      }
      if (this.pagination) this.pagination.hidden = Boolean(query);
      this.grid.hidden = Boolean(query) && visibleCount === 0;
    }

    clearSearch() {
      this.searchInput.value = '';
      this.update();
      this.searchInput.focus();
    }
  }

  customElements.define('collections-directory', CollectionsDirectory);
}
